import type { ClientEvent } from './queue';

export interface VisibilityOptions {
  enqueue: (e: ClientEvent) => void;
  flushBeacon: () => void;
  heartbeatIntervalMs?: number;
}

export function startVisibilityTracking(opts: VisibilityOptions): () => void {
  const interval = opts.heartbeatIntervalMs ?? 60_000;
  let visibleMs = 0;
  let lastVisibleStartMs = document.visibilityState === 'visible' ? Date.now() : null;

  function snapshotVisibleMs(): number {
    if (lastVisibleStartMs !== null) {
      const now = Date.now();
      visibleMs += now - lastVisibleStartMs;
      lastVisibleStartMs = now;
    }
    const out = Math.round(visibleMs);
    visibleMs = 0;
    return out;
  }

  function nowSec() { return Math.floor(Date.now() / 1000); }

  // session.start immediately.
  opts.enqueue({
    type: 'session.start',
    occurredAt: nowSec(),
    metadata: {
      ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '',
    },
  });

  function onVisibilityChange() {
    if (document.visibilityState === 'visible') {
      lastVisibleStartMs = Date.now();
    } else {
      if (lastVisibleStartMs !== null) {
        visibleMs += Date.now() - lastVisibleStartMs;
        lastVisibleStartMs = null;
      }
    }
  }

  function onPageHide() {
    opts.enqueue({
      type: 'session.end',
      occurredAt: nowSec(),
      visibleMs: snapshotVisibleMs(),
      reason: 'pagehide',
    });
    opts.flushBeacon();
  }

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);

  const timer = setInterval(() => {
    opts.enqueue({
      type: 'session.heartbeat',
      occurredAt: nowSec(),
      visibleMs: snapshotVisibleMs(),
    });
  }, interval);

  return () => {
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
  };
}
